import Papa from 'papaparse'
import { createStationOperationsReader } from '../Services/stationOperationsRequests'

export type StationOperationsCsvRow = Record<string, string | number | null | undefined>

// Export requests share the existing session checks and request pacing, without
// retaining old pages between separate exports.
const exportReader = createStationOperationsReader({ cacheMs: 0 })

export async function collectStationOperationsRows<T extends { id: string }, Q extends object>(
  key: string,
  load: (query: Q & { page: number; limit: number }) => Promise<{ data: T[]; total: number }>,
  filters: Q,
): Promise<T[]> {
  const rows: T[] = []
  const ids = new Set<string>()
  const limit = 50
  let total = Infinity

  for (let page = 1; rows.length < total; page++) {
    const query = { ...filters, page, limit }
    const result = await exportReader.read(`${key}:${JSON.stringify(query)}`, () => load(query))
    if (!Number.isSafeInteger(result.total) || result.total < 0) {
      throw new Error('Could not verify the export total. Please try again.')
    }
    if (page === 1) total = result.total
    if (result.total !== total || (!result.data.length && rows.length < total)) {
      throw new Error('The results changed during export. Please try again.')
    }
    for (const row of result.data) {
      if (ids.has(row.id)) throw new Error('The results changed during export. Please try again.')
      ids.add(row.id)
      rows.push(row)
    }
  }
  if (rows.length !== total) throw new Error('The export is incomplete. Please try again.')
  return rows
}

export function downloadStationOperationsCsv(rows: StationOperationsCsvRow[], filename: string) {
  const csv = Papa.unparse(rows, { escapeFormulae: true })
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }))
  const link = document.createElement('a')
  link.href = url
  link.download = `${filename}_${new Date().toISOString().slice(0, 10)}.csv`
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}
